import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useCollections } from "@/contexts/collections";
import AddCollectionButton from "@/components/common/AddCollectionButton";
import { INBOX_ID, ARCHIVE_ID } from "@/utils/contentFormUtils";
import { useContentCount } from "@/hooks/useContentCount";
import { useDragAndDrop } from "@/hooks/useDragAndDrop";
import { SidebarMenu } from "@/components/ui/sidebar/components/menu";
import { SidebarGroupContent } from "@/components/ui/sidebar/components/SidebarGroup";
import SidebarSection from "@/components/ui/sidebar/components/SidebarSection";
import CollectionList from "@/components/collections/CollectionList";

const SidebarContentSection = () => {
  const location = useLocation();
  const { totalCount, isLoading: isCountLoading } = useContentCount();
  const { 
    collections, 
    isLoading, 
    startEditCollection, 
    startDeleteCollection,
    getCollectionById,
    refreshCollections,
    updateCollectionPosition,
    toggleCollectionVisibility
  } = useCollections();
  
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  const [contentDropTargetId, setContentDropTargetId] = useState<string | null>(null);
  
  const [isCollectionsOpen, setIsCollectionsOpen] = useState(() => {
    const savedState = localStorage.getItem('sidebar-collections-open');
    return savedState !== null ? savedState === 'true' : true;
  });
  
  const { 
    handleDragOver, 
    handleDropOnCollection,
    isDragging: isContentDragging
  } = useDragAndDrop();

  useEffect(() => {
    localStorage.setItem('sidebar-collections-open', isCollectionsOpen.toString());
  }, [isCollectionsOpen]);

  useEffect(() => {
    console.log("SidebarContentSection mounted or route changed, refreshing collections");
    refreshCollections();
  }, [refreshCollections, location.pathname]);

  const handleEdit = useCallback((id: string) => {
    const collection = getCollectionById(id);
    if (collection) {
      startEditCollection(collection);
    } else {
      console.error(`Collection with id ${id} not found for editing`);
    }
  }, [getCollectionById, startEditCollection]);

  const handleDelete = useCallback((id: string) => {
    const collection = getCollectionById(id);
    if (collection) {
      startDeleteCollection(collection);
    } else {
      console.error(`Collection with id ${id} not found for deletion`);
    }
  }, [getCollectionById, startDeleteCollection]);
  
  const handleToggleVisibility = useCallback((id: string) => {
    toggleCollectionVisibility(id);
  }, [toggleCollectionVisibility]);
  
  const handleDragStart = (e: React.DragEvent, id: string) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
    setDraggingId(id);
    setIsDragging(true);
    
    setTimeout(() => {
      setIsDragging(true);
    }, 0);
  };
  
  const handleCollectionDragOver = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    try {
      const jsonData = e.dataTransfer.getData('application/json');
      if (jsonData) {
        setContentDropTargetId(targetId);
        return;
      }
    } catch (err) {
      if (isContentDragging) {
        setContentDropTargetId(targetId);
        return;
      }
    }
    
    if (targetId !== draggingId) {
      setDropTargetId(targetId);
    } else {
      setDropTargetId(null);
    }
  };
  
  const handleDragLeave = () => {
    setDropTargetId(null);
    setContentDropTargetId(null);
  };
  
  const handleDrop = async (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    
    try {
      const jsonData = e.dataTransfer.getData('application/json');
      if (jsonData) {
        handleDropOnCollection(e, targetId);
        setContentDropTargetId(null);
        return;
      }
    } catch (err) {
      console.error("Error checking drag data type:", err);
    }
    
    const sourceId = e.dataTransfer.getData('text/plain');
    
    if (sourceId === targetId) {
      setDraggingId(null);
      setIsDragging(false);
      setDropTargetId(null);
      return;
    }
    
    const sourceCollection = getCollectionById(sourceId);
    const targetCollection = getCollectionById(targetId);
    
    if (sourceCollection && targetCollection) {
      console.log(`Moving collection ${sourceId} to position ${targetCollection.position}`);
      await updateCollectionPosition(sourceId, targetCollection.position);
    }
    
    setDraggingId(null);
    setIsDragging(false);
    setDropTargetId(null);
  };
  
  const handleDragEnd = () => {
    setDraggingId(null);
    setIsDragging(false);
    setDropTargetId(null);
    setContentDropTargetId(null);
  };

  const handleContentDragOverWithState = (e: React.DragEvent, id: string) => {
    handleDragOver(e);
    setContentDropTargetId(id);
  };
  
  const handleContentDrop = (e: React.DragEvent, id: string) => {
    handleDropOnCollection(e, id);
    setContentDropTargetId(null);
  };

  return (
    <div className="w-full mt-6">
      <SidebarSection 
        title="Collections"
        isOpen={isCollectionsOpen}
        onOpenChange={setIsCollectionsOpen}
        action={
          <AddCollectionButton 
            variant="circle"
            showHoverText={true}
          />
        }
      >
        <SidebarGroupContent className="py-0">
          <SidebarMenu>
            <CollectionList
              collections={collections}
              isLoading={isLoading}
              totalCount={totalCount}
              isCountLoading={isCountLoading}
              draggingId={draggingId}
              dropTargetId={dropTargetId}
              contentDropTargetId={contentDropTargetId}
              onDragStart={handleDragStart}
              onDragOver={handleCollectionDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onDragEnd={handleDragEnd}
              onContentDragOver={handleContentDragOverWithState}
              onContentDrop={handleContentDrop}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggleVisibility={handleToggleVisibility}
            />
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarSection>
    </div>
  );
};

export default SidebarContentSection;
